/**
 * O DOCUMENTO — tudo o que não é texto: papel, margens, timbrado e numeração.
 *
 * Fica fora do HTML do corpo de propósito. O corpo é o que se cola num
 * e-mail ou num campo de petição; o documento é como a peça sai impressa.
 * O app hospedeiro guarda os dois juntos (`valor` e `documento`).
 */

import type { AjustesDosEstilosProntos } from './estilos';

export type Papel = 'a4' | 'oficio' | 'carta';
export type Orientacao = 'retrato' | 'paisagem';

export const PAPEIS: Record<Papel, { nome: string; largura: number; altura: number }> = {
  a4: { nome: 'A4', largura: 21, altura: 29.7 },
  oficio: { nome: 'Ofício', largura: 21.6, altura: 33 },
  carta: { nome: 'Carta', largura: 21.6, altura: 27.9 },
};

/** Margens em cm. */
export interface Margens { superior: number; inferior: number; esquerda: number; direita: number }

export const MARGENS_PRONTAS: { chave: string; nome: string; resumo: string; margens: Margens }[] = [
  { chave: 'abnt', nome: 'ABNT', resumo: 'sup. e esq. 3 cm, inf. e dir. 2 cm', margens: { superior: 3, inferior: 2, esquerda: 3, direita: 2 } },
  { chave: 'normal', nome: 'Normal', resumo: '2,5 cm em cima e embaixo, 3 cm dos lados', margens: { superior: 2.5, inferior: 2.5, esquerda: 3, direita: 3 } },
  { chave: 'estreita', nome: 'Estreita', resumo: '1,27 cm em todos os lados', margens: { superior: 1.27, inferior: 1.27, esquerda: 1.27, direita: 1.27 } },
];

/**
 * Papel timbrado. Montado com os dados do escritório, ou com as faixas
 * prontas (imagens) que a gráfica ou o Word já desenharam.
 */
export interface Timbrado {
  id: string;
  /** Como aparece no seletor: "Santos & Lima Advogados". */
  rotulo: string;
  /**
   * `dados` — campos (nome, OAB, endereço, contato) montados pelo editor;
   * `imagem` — faixas prontas de cabeçalho e rodapé;
   * `texto` — cabeçalho e rodapé em texto livre, várias linhas (a 1ª em negrito).
   */
  modo: 'dados' | 'imagem' | 'texto';
  nome?: string;
  oab?: string;
  endereco?: string;
  contato?: string;
  /** URL ou data: URI. */
  logotipo?: string;
  imagemCabecalho?: string;
  imagemRodape?: string;
  /** Modo `texto`. */
  cabecalhoTexto?: string;
  rodapeTexto?: string;
  /** Gerido pelo app em outro lugar: o painel mostra, não edita. */
  somenteLeitura?: boolean;
  /** Onde se edita, quando `somenteLeitura` ("Em Configurações → Timbrado"). */
  aviso?: string;
  /** Texto em diagonal ao fundo de cada página ("MINUTA"); edita-se em Configurações. */
  marcaDagua?: string;
}

/** Rótulo de um timbrado salvo, sem depender do modo. */
export function rotuloDoTimbrado(t: Timbrado): string {
  const primeira = (t.cabecalhoTexto ?? '').split('\n').find((l) => l.trim());
  return (t.modo === 'texto' ? primeira : t.nome)?.trim() || t.rotulo || 'Timbrado';
}

export interface NumeracaoPaginas {
  ativa: boolean;
  posicao: 'rodapeDireita' | 'rodapeCentro' | 'cabecalhoDireita';
  formato: 'paginaDeTotal' | 'numero' | 'traco';
  inicio: number;
  primeiraSemNumero: boolean;
}

/** "fls." no canto superior direito — para continuar a numeração dos autos. */
export interface NumeracaoFolhas {
  ativa: boolean;
  formato: 'fls' | 'fl' | 'folha';
  inicio: number;
}

export interface DocumentoForense {
  papel: Papel;
  orientacao: Orientacao;
  margens: Margens;
  nomeMargens: string;
  /** Id do timbrado em uso; null = sem timbrado. */
  timbrado: string | null;
  /** Texto livre por cima do timbrado (HTML curto). Vazio = só o timbrado. */
  cabecalho: string;
  rodape: string;
  paginas: NumeracaoPaginas;
  folhas: NumeracaoFolhas;
  /** Colunas do corpo (1, 2 ou 3) e a linha entre elas. */
  colunas: number;
  linhaEntreColunas: boolean;
  /**
   * O "Corpo da peça" (e os demais prontos) como o autor o quer nesta peça: o seu
   * "só para mim", gravado na peça para ela sair igual para quem a abrir e no PDF.
   * O ajuste do escritório vale por cima. Ausente: só o do escritório.
   */
  ajustesProntos?: AjustesDosEstilosProntos;
}

export const DOCUMENTO_PADRAO: DocumentoForense = {
  papel: 'a4',
  orientacao: 'retrato',
  margens: { superior: 3, inferior: 2, esquerda: 3, direita: 2 },
  nomeMargens: 'ABNT',
  timbrado: null,
  cabecalho: '',
  rodape: '',
  paginas: { ativa: true, posicao: 'rodapeDireita', formato: 'paginaDeTotal', inicio: 1, primeiraSemNumero: false },
  folhas: { ativa: false, formato: 'fls', inicio: 1 },
  colunas: 1,
  linhaEntreColunas: false,
};

/** A forma da folha que vale como padrão das peças novas (o resto é de cada peça). */
export type PaginaPadrao = Pick<DocumentoForense, 'papel' | 'orientacao' | 'margens' | 'nomeMargens'>;

export function paginaDoDocumento(d: DocumentoForense): PaginaPadrao {
  return { papel: d.papel, orientacao: d.orientacao, margens: { ...d.margens }, nomeMargens: d.nomeMargens };
}

export function mesmaPagina(a: PaginaPadrao | null | undefined, b: PaginaPadrao | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.papel === b.papel && a.orientacao === b.orientacao
    && (['superior', 'inferior', 'esquerda', 'direita'] as const).every((k) => a.margens[k] === b.margens[k]);
}

export function larguraDoPapel(d: DocumentoForense): number {
  const p = PAPEIS[d.papel];
  return d.orientacao === 'paisagem' ? p.altura : p.largura;
}

export function alturaDoPapel(d: DocumentoForense): number {
  const p = PAPEIS[d.papel];
  return d.orientacao === 'paisagem' ? p.largura : p.altura;
}

export function textoNumeroPagina(d: DocumentoForense, indice: number, total: number): string | null {
  const n = d.paginas;
  if (!n.ativa || (n.primeiraSemNumero && indice === 0)) return null;
  const numero = n.inicio + indice;
  if (n.formato === 'numero') return String(numero);
  if (n.formato === 'traco') return `– ${numero} –`;
  return `Página ${numero} de ${n.inicio + total - 1}`;
}

export function textoFolha(d: DocumentoForense, indice: number): string | null {
  const f = d.folhas;
  if (!f.ativa) return null;
  const n = f.inicio + indice;
  return f.formato === 'fl' ? `Fl. ${n}` : f.formato === 'folha' ? `Folha ${n}` : `fls. ${n}`;
}
